import { useEffect, useRef, useState } from "react";
import { ESPLoader, Transport } from "esptool-js";
import { Usb, Zap, X, AlertTriangle, CheckCircle2, Cable } from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Web Serial Flash modal.
 *
 * Pulls a flash-bundle (bootloader + partitions + boot_app0 + firmware) from the API,
 * then uses esptool-js to write all 4 partitions to a USB-connected ESP32 via the browser's
 * Web Serial API. Works in Chrome/Edge (desktop) only.
 *
 * Props: projectId, version (the compile-XXX version string), open, onClose
 */
export default function FlashWebSerialModal({ projectId, version, open, onClose }) {
  const [phase, setPhase] = useState("idle"); // idle | bundle-loading | bundle-ready | port-connecting | flashing | done | error
  const [progress, setProgress] = useState(0);
  const [bundle, setBundle] = useState(null); // {chip, version, files: [{name, address, data_base64, size}]}
  const [logLines, setLogLines] = useState([]);
  const [error, setError] = useState("");
  const transportRef = useRef(null);
  const esploaderRef = useRef(null);

  const supported = typeof navigator !== "undefined" && "serial" in navigator;

  const appendLog = (line) => setLogLines((p) => [...p.slice(-200), line]);

  // Reset state when modal opens
  useEffect(() => {
    if (!open) return;
    setPhase("idle");
    setProgress(0);
    setBundle(null);
    setLogLines([]);
    setError("");
  }, [open, version]);

  const loadBundle = async () => {
    setPhase("bundle-loading");
    setError("");
    try {
      const { data } = await api.get(`/projects/${projectId}/firmware/flash-bundle/${version}`);
      const totalKB = data.files.reduce((a, f) => a + f.size, 0) / 1024;
      appendLog(`Bundle dimuat: ${data.files.length} partisi · ${totalKB.toFixed(1)} KB total`);
      data.files.forEach((f) =>
        appendLog(`  ${f.name.padEnd(11)} → 0x${f.address.toString(16).padStart(5, "0")}  (${(f.size / 1024).toFixed(1)} KB)`)
      );
      setBundle(data);
      setPhase("bundle-ready");
    } catch (e) {
      setError(e?.response?.data?.detail || "Gagal memuat firmware bundle");
      setPhase("error");
    }
  };

  // base64 → binary string (esptool-js expects "binaryString" not Uint8Array)
  const b64ToBinaryString = (b64) => {
    const raw = atob(b64);
    return raw;
  };

  const flash = async () => {
    if (!bundle) return;
    setError("");
    setPhase("port-connecting");
    setProgress(0);
    setLogLines([]);
    appendLog("Meminta izin akses USB Serial dari browser…");

    let port;
    try {
      port = await navigator.serial.requestPort();
    } catch (e) {
      setError("User membatalkan pemilihan port USB.");
      setPhase("bundle-ready");
      return;
    }

    try {
      const transport = new Transport(port, true);
      transportRef.current = transport;
      const espLoader = new ESPLoader({
        transport,
        baudrate: bundle.baudrate_recommended || 921600,
        romBaudrate: 115200,
        terminal: {
          clean: () => {},
          writeLine: (d) => appendLog(d),
          write: (d) => appendLog(d.toString().replace(/\r?\n$/, "")),
        },
      });
      esploaderRef.current = espLoader;

      appendLog("Menghubungkan ke ESP32 melalui USB (mode bootloader)...");
      const chipDesc = await espLoader.main();
      appendLog(`Chip terdeteksi: ${chipDesc}`);

      setPhase("flashing");
      const fileArray = bundle.files.map((f) => ({
        data: b64ToBinaryString(f.data_base64),
        address: f.address,
      }));

      const totalBytes = bundle.files.reduce((a, f) => a + f.size, 0);
      const writtenPerFile = bundle.files.map(() => 0);

      await espLoader.writeFlash({
        fileArray,
        flashSize: "keep",
        flashMode: "keep",
        flashFreq: "keep",
        eraseAll: false,
        compress: true,
        reportProgress: (idx, written /* current file bytes done */) => {
          writtenPerFile[idx] = written;
          const totalDone = writtenPerFile.reduce((a, b) => a + b, 0);
          setProgress(Math.min(100, (totalDone / totalBytes) * 100));
        },
      });

      await espLoader.after();
      appendLog("Flash selesai. ESP32 di-reset.");
      setProgress(100);
      setPhase("done");
      toast.success("Flash via USB berhasil!");
    } catch (e) {
      console.error(e);
      const msg = e?.message || String(e);
      appendLog(`[ERROR] ${msg}`);
      setError(msg);
      setPhase("error");
    } finally {
      try {
        if (transportRef.current) await transportRef.current.disconnect();
      } catch (_) { /* ignore disconnect errors */ }
      transportRef.current = null;
      esploaderRef.current = null;
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="flash-modal"
      role="dialog"
    >
      <div className="bg-card text-card-foreground border border-border rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Usb className="w-5 h-5 text-primary" />
            <h2 className="font-display text-lg font-semibold">Flash via USB Web Serial</h2>
            {version && (
              <Badge variant="outline" className="border-primary/40 text-primary font-mono text-xs">
                {version}
              </Badge>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="flash-close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!supported && (
            <div className="border border-destructive/40 bg-destructive/10 text-destructive rounded-md p-3 flex gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Browser tidak mendukung Web Serial API.</p>
                <p className="text-xs opacity-80 mt-1">
                  Gunakan Chrome atau Edge versi desktop (Android/iOS &amp; Firefox belum mendukung). Pastikan halaman dilayani melalui HTTPS.
                </p>
              </div>
            </div>
          )}

          {supported && (
            <>
              <ol className="text-sm space-y-2 list-decimal list-inside text-muted-foreground">
                <li>Sambungkan ESP32 ke port USB komputer.</li>
                <li>Tahan tombol <span className="font-mono text-foreground">BOOT</span> pada board lalu klik <span className="font-mono text-foreground">EN/RST</span> agar board masuk mode programming (sekali saja).</li>
                <li>Klik &quot;Muat Bundle&quot; untuk download firmware bundle (bootloader + partitions + firmware).</li>
                <li>Klik &quot;Mulai Flash&quot; lalu pilih port serial yang muncul di browser.</li>
              </ol>

              {/* Bundle loading */}
              {phase === "idle" && (
                <Button onClick={loadBundle} className="w-full" data-testid="flash-load-bundle">
                  <Cable className="w-4 h-4 mr-2" /> Muat Bundle Firmware
                </Button>
              )}
              {phase === "bundle-loading" && (
                <div className="text-sm text-muted-foreground">Memuat bundle…</div>
              )}

              {bundle && (
                <div className="border border-border bg-muted/30 rounded-md p-3 text-xs font-mono space-y-1" data-testid="flash-bundle-info">
                  <div className="text-foreground font-sans font-medium mb-1">Bundle siap:</div>
                  {bundle.files.map((f) => (
                    <div key={f.name} className="flex justify-between">
                      <span>{f.name}</span>
                      <span>
                        @ 0x{f.address.toString(16).padStart(5, "0")} · {(f.size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {phase === "bundle-ready" && (
                <Button onClick={flash} className="w-full" data-testid="flash-start">
                  <Zap className="w-4 h-4 mr-2" /> Mulai Flash
                </Button>
              )}

              {(phase === "port-connecting" || phase === "flashing") && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {phase === "port-connecting" ? "Menghubungkan ke ESP32..." : "Menulis flash..."}
                    </span>
                    <span className="font-mono text-foreground">{progress.toFixed(1)}%</span>
                  </div>
                  <Progress value={progress} data-testid="flash-progress" />
                </div>
              )}

              {phase === "done" && (
                <div className="border border-primary/40 bg-primary/10 text-primary rounded-md p-3 flex gap-2 text-sm" data-testid="flash-done">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Flash berhasil!</p>
                    <p className="text-xs opacity-80">ESP32 di-reset dan langsung menjalankan firmware baru. Cek log Serial Monitor untuk konfirmasi.</p>
                  </div>
                </div>
              )}

              {error && (
                <div className="border border-destructive/40 bg-destructive/10 text-destructive rounded-md p-3 flex gap-2 text-sm" data-testid="flash-error">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Flash gagal</p>
                    <p className="text-xs opacity-80">{error}</p>
                  </div>
                </div>
              )}

              {logLines.length > 0 && (
                <details open className="border border-border rounded-md">
                  <summary className="px-3 py-2 cursor-pointer text-xs font-medium">Log proses</summary>
                  <pre
                    className="bg-muted/40 text-xs font-mono p-3 max-h-48 overflow-y-auto whitespace-pre-wrap"
                    data-testid="flash-log"
                  >
                    {logLines.join("\n")}
                  </pre>
                </details>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
